<template>
  <section
    class="rounded-lg p-5 space-y-5"
    :style="{ background: 'var(--bg-elev)', border: '1px solid var(--border)' }"
    data-test="phase-1b3-section"
  >
    <header class="flex items-end justify-between gap-3 flex-wrap">
      <div>
        <p class="text-xs uppercase tracking-wider" style="color: var(--fg-subtle)">
          Velocity & investment
        </p>
        <h2 class="text-lg font-semibold mt-0.5" data-test="velocity-header">
          {{ velocityHeader }}
        </h2>
      </div>
      <PredictabilityReadout
        :rate="predictability.rate"
        :within-count="predictability.withinCount"
        :qualifying-count="predictability.qualifyingCount"
      />
    </header>

    <!-- Velocity full-width -->
    <VelocityChart :series="velocity" />

    <!-- Investment mix two-up -->
    <div class="grid gap-5 md:grid-cols-2">
      <div
        class="rounded-md p-4"
        :style="{ background: 'var(--bg-card)', border: '1px solid var(--border)' }"
      >
        <p class="text-xs uppercase tracking-wider mb-3" style="color: var(--fg-subtle)">
          Latest mix
        </p>
        <InvestmentMixDonut :shares="latestShares" />
      </div>
      <div
        class="rounded-md p-4"
        :style="{ background: 'var(--bg-card)', border: '1px solid var(--border)' }"
      >
        <p class="text-xs uppercase tracking-wider mb-3" style="color: var(--fg-subtle)">
          {{ mixHeader }}
        </p>
        <InvestmentMixStackedArea :series="mix" />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from "vue";
import PredictabilityReadout from "@/components/PredictabilityReadout.vue";
import VelocityChart from "@/components/VelocityChart.vue";
import InvestmentMixDonut from "@/components/InvestmentMixDonut.vue";
import InvestmentMixStackedArea from "@/components/InvestmentMixStackedArea.vue";
import type { SprintListItem } from "@/api/types";
import { predictabilityRate, velocitySeries } from "@/lib/velocity";
import { extractMixShares, mixSeries } from "@/lib/investmentMix";

const props = withDefaults(
  defineProps<{
    sprints: SprintListItem[];
    window?: number;
  }>(),
  { window: 8 },
);

const velocity = computed(() => velocitySeries(props.sprints, props.window));
const predictability = computed(() => predictabilityRate(velocity.value));

const latestShares = computed(() => {
  const latest = props.sprints[0];
  return extractMixShares(latest?.headline_metrics?.M4);
});

const mix = computed(() => mixSeries(props.sprints, props.window));

// Header copy reflects the *actual* number of sprints rendered, not the
// window cap — DA has 3 closed sprints; saying "Last 8 closed sprints" is a
// lie that drifts further as projects with different histories share the SPA.
const velocityHeader = computed(() => {
  const n = velocity.value.length;
  if (n === 0) return "No closed sprints yet";
  if (n === 1) return "Last 1 closed sprint";
  return `Last ${n} closed sprints`;
});

const mixHeader = computed(() => {
  const n = mix.value.length;
  if (n === 0) return "Mix drift (no closed sprints)";
  return `Mix drift (${n} sprint${n === 1 ? "" : "s"})`;
});
</script>
